/**
 * Operations Domain Handlers
 *
 * Pure command handlers — `(state, command) => newState`. No side effects, no
 * dispatch. The backend commands (`ApproveOperation` / `RejectOperation`) are
 * issued by policies; submit/reject handlers only flip the submitting flag.
 */

import { CommandHandlerMap } from '@commandDrivenStateEngine/core/types';
import { OperationsState, buildDefaultRowApproval } from './state';
import { OperationsCommand } from './commands';

type ExtractCommand<T extends OperationsCommand['type']> = Extract<
  OperationsCommand,
  { type: T }
>;

// =============================================================================
// REVIEW HANDLERS
// =============================================================================

const handleLoadRun = (
  state: OperationsState,
  cmd: ExtractCommand<'operations/review/loadRun'>
): OperationsState => ({
  ...state,
  review: { ...state.review, run: cmd.payload.run },
  loading: { ...state.loading, run: false }
});

const handleSetChangeSet = (
  state: OperationsState,
  cmd: ExtractCommand<'operations/review/setChangeSet'>
): OperationsState => ({
  ...state,
  review: {
    ...state.review,
    changeSet: cmd.payload.changeSet,
    rowApproval: buildDefaultRowApproval( cmd.payload.changeSet )
  },
  loading: { ...state.loading, changeSet: false }
});

const handleToggleRowApproval = (
  state: OperationsState,
  cmd: ExtractCommand<'operations/review/toggleRowApproval'>
): OperationsState => {
  const { targetId, approved } = cmd.payload;
  const next = approved ?? !( state.review.rowApproval[targetId] === true );
  return {
    ...state,
    review: {
      ...state.review,
      rowApproval: { ...state.review.rowApproval, [targetId]: next }
    }
  };
};

const handleApproveAll = (
  state: OperationsState
): OperationsState => {
  const items = state.review.changeSet?.items ?? [];
  const rowApproval = items.reduce<Record<string, boolean>>(( acc, item ) => {
    // Only actionable rows become approved; non-actionable stay excluded.
    acc[item.targetId] = item.status === 'proposed' || item.status === 'approved';
    return acc;
  }, {});
  return {
    ...state,
    review: { ...state.review, rowApproval }
  };
};

const handleClearApproval = (
  state: OperationsState
): OperationsState => {
  const items = state.review.changeSet?.items ?? [];
  const rowApproval = items.reduce<Record<string, boolean>>(( acc, item ) => {
    acc[item.targetId] = false;
    return acc;
  }, {});
  return {
    ...state,
    review: { ...state.review, rowApproval }
  };
};

const handleSubmitApproval = (
  state: OperationsState
): OperationsState => ({
  ...state,
  loading: { ...state.loading, submitting: true }
});

const handleReject = (
  state: OperationsState
): OperationsState => ({
  ...state,
  loading: { ...state.loading, submitting: true }
});

const handleClear = (
  state: OperationsState
): OperationsState => ({
  ...state,
  review: { run: null, changeSet: null, rowApproval: {}},
  loading: { ...state.loading, run: false, changeSet: false, submitting: false }
});

// =============================================================================
// LIST HANDLERS
// =============================================================================

const handleSetRuns = (
  state: OperationsState,
  cmd: ExtractCommand<'operations/list/setRuns'>
): OperationsState => ({
  ...state,
  list: {
    ...state.list,
    runs: cmd.payload.runs,
    runsCount: cmd.payload.count
  },
  loading: { ...state.loading, history: false }
});

const handleSetPage = (
  state: OperationsState,
  cmd: ExtractCommand<'operations/list/setPage'>
): OperationsState => ({
  ...state,
  list: { ...state.list, currentPage: cmd.payload.page }
});

const handleSetPerPage = (
  state: OperationsState,
  cmd: ExtractCommand<'operations/list/setPerPage'>
): OperationsState => ({
  ...state,
  list: { ...state.list, perPage: cmd.payload.perPage, currentPage: 1 }
});

// =============================================================================
// LOADING HANDLERS
// =============================================================================

const handleSetRunLoading = (
  state: OperationsState,
  cmd: ExtractCommand<'operations/loading/setRun'>
): OperationsState => ({
  ...state,
  loading: { ...state.loading, run: cmd.payload.isLoading }
});

const handleSetChangeSetLoading = (
  state: OperationsState,
  cmd: ExtractCommand<'operations/loading/setChangeSet'>
): OperationsState => ({
  ...state,
  loading: { ...state.loading, changeSet: cmd.payload.isLoading }
});

const handleSetSubmittingLoading = (
  state: OperationsState,
  cmd: ExtractCommand<'operations/loading/setSubmitting'>
): OperationsState => ({
  ...state,
  loading: { ...state.loading, submitting: cmd.payload.isLoading }
});

const handleSetHistoryLoading = (
  state: OperationsState,
  cmd: ExtractCommand<'operations/loading/setHistory'>
): OperationsState => ({
  ...state,
  loading: { ...state.loading, history: cmd.payload.isLoading }
});

// =============================================================================
// HANDLER MAP
// =============================================================================

export const handlers: CommandHandlerMap<OperationsState, OperationsCommand> = {
  // Review
  'operations/review/loadRun': handleLoadRun,
  'operations/review/setChangeSet': handleSetChangeSet,
  'operations/review/toggleRowApproval': handleToggleRowApproval,
  'operations/review/approveAll': handleApproveAll,
  'operations/review/clearApproval': handleClearApproval,
  'operations/review/submitApproval': handleSubmitApproval,
  'operations/review/reject': handleReject,
  'operations/review/clear': handleClear,
  // List
  'operations/list/setRuns': handleSetRuns,
  'operations/list/setPage': handleSetPage,
  'operations/list/setPerPage': handleSetPerPage,
  // Loading
  'operations/loading/setRun': handleSetRunLoading,
  'operations/loading/setChangeSet': handleSetChangeSetLoading,
  'operations/loading/setSubmitting': handleSetSubmittingLoading,
  'operations/loading/setHistory': handleSetHistoryLoading
};
